import { FC, memo } from 'react';
import { ServiceStatusInfo } from '../../types/internal';
import { STORAGE_NODES } from '../../constants/infrastructure';
import { formatServiceName, formatUptime, getServiceIcon } from '../../utils/infrastructure';
import { InternalStatusPulse } from './InternalStatusPulse';
import { formatDistanceToNow } from 'date-fns';

interface InternalServiceRowProps {
    s: ServiceStatusInfo;
}

export const InternalServiceRow: FC<InternalServiceRowProps> = memo(({ s }) => {
    // DRY: Using exact match or prefix consistency (matching how infraNodes logic groups them)
    const isStorageNode = STORAGE_NODES.some(prefix => s.serviceName.startsWith(prefix));

    return (
        <div className="group flex items-center min-h-[90px] px-6 hover:bg-slate-50/80 transition-all border-b border-slate-100 last:border-0 overflow-hidden">
            {/* Column 1: Identity (Fixed Width) */}
            <div className="flex items-center gap-4 w-[280px] shrink-0">
                <div className="w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 group-hover:text-slate-600 transition-colors shadow-sm shrink-0">
                    {getServiceIcon(s.serviceName)}
                </div>
                <div className="flex flex-col min-w-0 pr-4">
                    <span className="text-[14px] font-bold text-slate-800 leading-tight truncate">
                        {formatServiceName(s.serviceName)}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5 truncate">
                        {s.serviceName}
                    </span>
                </div>
            </div>

            {/* Column 2: Status (Fixed Width) */}
            <div className="w-[150px] shrink-0 border-l border-slate-50 items-center flex px-4">
                <InternalStatusPulse active={s.isActive} />
            </div>

            {/* Column 3: Telemetry (Fluid Width) */}
            <div className="flex items-center gap-10 flex-1 px-6 border-l border-slate-50">
                {/* Cluster A: Resources */}
                <div className="flex flex-col min-w-[120px]">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1.5">
                        {isStorageNode ? 'Size on Disk' : 'Working Memory'}
                    </span>
                    <span className="text-[13px] font-mono font-bold text-slate-700">
                        {s.memory?.rss ? `${s.memory.rss.toFixed(1)} MB` : 'N/A'}
                    </span>
                </div>

                {/* Cluster B: Processing Load */}
                {s.templateCount !== null && (
                    <div className="flex flex-col min-w-[120px]">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1.5">Template Load</span>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-mono font-bold text-slate-700">{s.templateCount}</span>
                            <span className="text-[10px] font-bold text-slate-300 uppercase">Items In-Sync</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Column 4: Service Uptime  */}
            <div className="flex flex-col min-w-[120px] px-6 border-l border-slate-50 justify-center">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1.5">Uptime</span>
                <span className="text-[13px] font-mono font-bold text-slate-700">
                    {s.uptime ? formatUptime(s.uptime) : 'N/A'}
                </span>
            </div>

            {/* Column 5: Synchronization  */}
            <div className="flex flex-col items-end text-right w-[160px] pl-6 justify-center">
                <span className="text-[12px] font-medium text-slate-600">
                    {s.lastHeartbeat ? formatDistanceToNow(new Date(s.lastHeartbeat), { addSuffix: true }) : 'N/A'}
                </span>
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.1em] mt-0.5">Last Sync</span>
            </div>
        </div>
    );
});
InternalServiceRow.displayName = 'InternalServiceRow';
